import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HeaderStatusBar, LiveIndicator, ThreatLevelChip, type LinkState, type ThreatLevel } from '@/components/ds-dlp';
import { toSeverityTone } from '@/lib/tone';
import { alertsApi } from '../api/client';

interface Alert {
  severity?: string;
  status?: string;
  created_at?: string;
}

const RANK: ThreatLevel[] = ['low', 'medium', 'high', 'critical'];
const DAY_MS = 24 * 60 * 60 * 1000;

/** The highest severity among alerts still open from the last 24 hours (Low when none). */
export function threatLevelOf(alerts: Alert[], now = Date.now()): { level: ThreatLevel; count: number } {
  const recent = alerts.filter((a) => {
    if (a.status && a.status !== 'open') return false;
    const t = a.created_at ? Date.parse(a.created_at) : NaN;
    return Number.isNaN(t) || now - t <= DAY_MS;
  });
  let level = 0;
  for (const a of recent) {
    const i = RANK.indexOf(toSeverityTone(a.severity) as ThreatLevel);
    if (i > level) level = i;
  }
  return { level: RANK[level], count: recent.length };
}

function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/**
 * The top bar's threat level and live-data indicator. One poll of the open
 * alerts (every 30 seconds) drives both: the threat level from what it
 * returns, and Live from whether it arrived. A poll the server answered, even
 * with an error status, still proves the connection; only a request that got
 * no answer, or a browser without network, takes it off Live. If this role
 * may not read alerts, the threat level is not shown.
 */
export function HeaderStatus() {
  const navigate = useNavigate();
  const online = useOnline();
  const { data, error, isError, dataUpdatedAt, errorUpdatedAt } = useQuery({
    queryKey: ['header-status', 'open-alerts'],
    queryFn: () => alertsApi.getAll('open').then((r) => (Array.isArray(r.data) ? (r.data as Alert[]) : [])),
    refetchInterval: 30_000,
    retry: false,
  });

  const answered = !isError || (axios.isAxiosError(error) && !!error.response);
  const forbidden = axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403);
  const state: LinkState = !online ? 'offline' : answered ? 'live' : 'reconnecting';
  const last = Math.max(dataUpdatedAt, answered ? errorUpdatedAt : 0);
  const threat = useMemo(() => threatLevelOf(data ?? []), [data]);

  return (
    <HeaderStatusBar className="hidden sm:flex">
      <LiveIndicator state={state} lastUpdated={last ? new Date(last) : null} />
      {!forbidden && data ? (
        <ThreatLevelChip level={threat.level} openAlerts={threat.count} onClick={() => navigate('/dashboard')} className="hidden md:inline-flex" />
      ) : null}
    </HeaderStatusBar>
  );
}
